import { TableCell } from '@material-ui/core'
import React from 'react'

import {
  BAND_OF,
  COL_BANDS,
  COL_VIEWS,
  LEAD_WIDTH,
  PINNED_WIDTH
} from './sheetColumns'

/* Collapsible column bands, shared by the two grids that render the daily-sheet
   column set — At Apartment → Requests and HQ → Daily Sheet.

   They deliberately share `SHEET_COLS` ("keep this format intact", 2026-07-17),
   so a capability on one of them belongs on both; only At-Apartment-specific
   behaviour should diverge. This hook is the core half: it owns the fold state,
   the display sequence and the pin geometry. Presentation stays in each grid,
   because their cell classes and their leading/trailing columns differ.

   The hook returns:
     displayCols   [{kind:'col', col} | {kind:'stub', band}] — map over THIS,
                   never the raw column list, or the header and the body drift
                   apart and leave a seam.
     pinFor(col)   {left, width} for a pinned column, or null.
     lastPinned    key of the right-most pinned column (draw the edge shadow on it).
     foldedBands   Set of folded band ids.
     toggleBand    fold / unfold one band.
     applyColView  apply a named view.
     activeColView the matching view id, or '' when the mix is custom.
*/
export default function useColumnBands(
  cols,
  storageKey,
  pinnedKeys = [],
  // Width of the cells BEFORE the first data column, which the two grids do
  // not share: At Apartment → Requests has an expander plus a checkbox, the HQ
  // daily sheet has only a checkbox. Passing it in keeps the pin offsets right
  // in both rather than baking one grid's layout into the shared hook.
  leadWidth = LEAD_WIDTH,
  // The band list, so a grid can carry a band of its own on top of the shared
  // set (the At Apartment grid folds Date away — the day picker above it
  // already names the day; the HQ daily sheet keeps Date fixed). Pass the
  // matching `bandOf` to `bandHeaderRow` too — it is returned from here.
  bands = COL_BANDS
) {
  // Starts empty and loads from localStorage AFTER mount: reading it in the
  // initialiser would make the first render depend on storage that can throw
  // (private mode), and the preference is per browser anyway — never shared,
  // never sent anywhere.
  const [foldedBands, setFoldedBands] = React.useState(() => new Set())

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey)
      if (raw) setFoldedBands(new Set(raw.split(',').filter(Boolean)))
    } catch (e) {
      /* storage disabled — start with everything unfolded */
    }
  }, [storageKey])

  const persist = React.useCallback(
    (next) => {
      try {
        window.localStorage.setItem(storageKey, Array.from(next).join(','))
      } catch (e) {
        /* non-fatal: the fold still applies for this session */
      }
      return next
    },
    [storageKey]
  )

  const toggleBand = React.useCallback(
    (id) =>
      setFoldedBands((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return persist(next)
      }),
    [persist]
  )

  const applyColView = React.useCallback(
    (view) => setFoldedBands(persist(new Set(view.fold))),
    [persist]
  )

  const activeColView = React.useMemo(() => {
    const found = COL_VIEWS.find(
      (v) =>
        v.fold.length === foldedBands.size &&
        v.fold.every((b) => foldedBands.has(b))
    )
    return found ? found.id : ''
  }, [foldedBands])

  // column key -> band, for THIS grid's band list.
  const bandOf = React.useMemo(() => {
    if (bands === COL_BANDS) return BAND_OF
    const map = {}
    bands.forEach((b) => {
      b.cols.forEach((k) => {
        map[k] = b
      })
    })
    return map
  }, [bands])

  // Normal columns, with each folded band replaced by ONE stub marker.
  const displayCols = React.useMemo(() => {
    const seq = []
    let lastStub = null
    cols.forEach((col) => {
      const band = bandOf[col.k]
      if (band && foldedBands.has(band.id)) {
        if (lastStub !== band.id) {
          seq.push({ kind: 'stub', band })
          lastStub = band.id
        }
        return
      }
      lastStub = null
      seq.push({ kind: 'col', col })
    })
    return seq
  }, [cols, foldedBands, bandOf])

  // Cumulative left offsets, recomputed with the sequence so folding a band
  // that sits between two pinned columns cannot leave a gap.
  const pins = React.useMemo(() => {
    const offsets = {}
    let left = leadWidth
    let last = null
    displayCols.forEach((d) => {
      if (d.kind !== 'col' || !pinnedKeys.includes(d.col.k)) return
      const width = PINNED_WIDTH[d.col.k] || d.col.width || 120
      offsets[d.col.k] = { left, width }
      left += width
      last = d.col.k
    })
    return { offsets, last }
  }, [displayCols, pinnedKeys, leadWidth])

  const pinFor = React.useCallback((col) => pins.offsets[col.k] || null, [pins])

  return {
    bands,
    bandOf,
    views: COL_VIEWS,
    displayCols,
    pinFor,
    lastPinned: pins.last,
    foldedBands,
    toggleBand,
    applyColView,
    activeColView
  }
}

/* The band header row for a display sequence: a run of banded columns becomes
   one colSpan header, a folded band becomes its stub, and everything ungrouped
   becomes a blank spacer so the row still spans the whole table.

   `classes` must supply `stubCell`, `stubBodyCell`, `stubText`, `bandCell`
   and `bandRowSpacer`. */
export function bandHeaderRow(
  displayCols,
  classes,
  toggleBand,
  bandOf = BAND_OF
) {
  const cells = []
  let i = 0
  while (i < displayCols.length) {
    const d = displayCols[i]
    if (d.kind === 'stub') {
      cells.push(
        <TableCell
          key={`bstub-${d.band.id}`}
          className={classes.stubCell}
          onClick={() => toggleBand(d.band.id)}
          title={`Unfold ${d.band.label} (${d.band.cols.length} columns)`}
        >
          <span className={classes.stubText}>›</span>
        </TableCell>
      )
      i += 1
      continue
    }
    const band = bandOf[d.col.k]
    if (band) {
      let span = 0
      while (
        i + span < displayCols.length &&
        displayCols[i + span].kind === 'col' &&
        bandOf[displayCols[i + span].col.k] === band
      ) {
        span += 1
      }
      cells.push(
        <TableCell
          key={`band-${band.id}`}
          colSpan={span}
          className={classes.bandCell}
          onClick={() => toggleBand(band.id)}
          title={`Fold ${band.label} away`}
        >
          {band.label} ▾
        </TableCell>
      )
      i += span
      continue
    }
    let plain = 0
    while (
      i + plain < displayCols.length &&
      displayCols[i + plain].kind === 'col' &&
      !bandOf[displayCols[i + plain].col.k]
    ) {
      plain += 1
    }
    cells.push(
      <TableCell
        key={`plain-${i}`}
        colSpan={plain}
        className={classes.bandRowSpacer}
      />
    )
    i += plain
  }
  return cells
}

/* Map a display sequence to cells. `fn(col)` renders a real column; a folded
   band yields its clickable stub. Every row in the grid must go through this,
   or a folded band shifts one row's cells and not another's. */
export function mapBandedCells(displayCols, classes, toggleBand, fn) {
  return displayCols.map((d) =>
    d.kind === 'stub' ? (
      <TableCell
        key={`stub-${d.band.id}`}
        // Body cells keep the row's fill — see `stubBodyCell`. Only the header
        // carries the orange, or a folded band paints a stripe down the table.
        className={classes.stubBodyCell || classes.stubCell}
        onClick={() => toggleBand(d.band.id)}
        title={`Unfold ${d.band.label} (${d.band.cols.length} columns)`}
      />
    ) : (
      fn(d.col)
    )
  )
}
